import React from 'react';
import { Link } from 'react-router-dom';
import { ChefHat, Clock, Users, Leaf } from 'lucide-react';
import linkingHelper from '../../utils/linkingHelper';

interface Recipe {
  id: string;
  title?: string;
  name?: string;
  description: string;
  ingredients: any[];
  primaryProduce: string[];
  prepTime: number | string;
  cookTime: number | string;
  servings: number;
  difficulty: 'easy' | 'medium' | 'hard';
  cuisine?: string;
  dietaryTags: string[];
  instructions: string[];
  nutritionInfo?: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
  };
}

interface RecipeDetailsProps {
  recipe: Recipe;
}

const RecipeDetails: React.FC<RecipeDetailsProps> = ({ recipe }) => {
  const parseTime = (time: number | string): number => {
    if (typeof time === 'number') return time;
    if (typeof time === 'string') {
      const match = time.match(/(\d+)/);
      return match ? parseInt(match[1]) : 0;
    }
    return 0;
  };

  const prepTime = parseTime(recipe.prepTime);
  const cookTime = parseTime(recipe.cookTime);
  const totalTime = prepTime + cookTime;

  const styles = {
    container: {
      backgroundColor: '#fff',
      borderRadius: '8px',
      padding: '32px',
      marginTop: '24px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '24px',
      paddingBottom: '16px',
      borderBottom: '2px solid #2D5A27',
    },
    headerIcon: {
      color: '#2D5A27',
      marginRight: '12px',
    },
    title: {
      fontSize: '24px',
      fontWeight: '600',
      color: '#1a1a1a',
      margin: 0,
    },
    metaGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: '16px',
      marginBottom: '32px',
    },
    metaItem: {
      textAlign: 'center' as const,
      padding: '16px',
      backgroundColor: '#f9f9f9',
      borderRadius: '8px',
    },
    metaIcon: {
      color: '#2D5A27',
      marginBottom: '8px',
    },
    metaLabel: {
      fontSize: '12px',
      color: '#666',
      textTransform: 'uppercase' as const,
      fontWeight: '600',
      marginBottom: '4px',
    },
    metaValue: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#1a1a1a',
    },
    section: {
      marginBottom: '32px',
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#1a1a1a',
      marginBottom: '16px',
      display: 'flex',
      alignItems: 'center',
    },
    sectionIcon: {
      marginRight: '10px',
      color: '#2D5A27',
    },
    description: {
      fontSize: '16px',
      lineHeight: '1.8',
      color: '#333',
      marginBottom: '32px',
    },
    ingredientList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
      gap: '12px',
    },
    ingredientItem: {
      padding: '12px 16px',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
      fontSize: '15px',
      color: '#333',
      display: 'flex',
      alignItems: 'center',
      transition: 'all 0.3s ease',
    } as React.CSSProperties,
    ingredientItemHover: {
      backgroundColor: '#f0f7ef',
      transform: 'translateX(4px)',
    },
    ingredientBullet: {
      width: '6px',
      height: '6px',
      backgroundColor: '#2D5A27',
      borderRadius: '50%',
      marginRight: '12px',
      flexShrink: 0,
    },
    produceLink: {
      color: '#2D5A27',
      fontWeight: '600',
      textDecoration: 'none',
      transition: 'color 0.3s ease',
    },
    produceLinkHover: {
      color: '#1a3d1a',
      textDecoration: 'underline',
    },
    instructionList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      counterReset: 'step-counter',
    },
    instructionItem: {
      padding: '16px',
      backgroundColor: '#f9f9f9',
      borderRadius: '6px',
      marginBottom: '12px',
      fontSize: '15px',
      lineHeight: '1.7',
      color: '#333',
      position: 'relative' as const,
      paddingLeft: '56px',
      counterIncrement: 'step-counter',
    } as React.CSSProperties,
    instructionNumber: {
      position: 'absolute' as const,
      left: '16px',
      top: '16px',
      width: '28px',
      height: '28px',
      backgroundColor: '#2D5A27',
      color: '#fff',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: '600',
    },
    tagsContainer: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
      marginTop: '24px',
    },
    tag: {
      padding: '6px 14px',
      backgroundColor: '#f0f7ef',
      color: '#2D5A27',
      borderRadius: '16px',
      fontSize: '13px',
      fontWeight: '600',
      border: '1px solid #d4e7d2',
    },
    produceTagsContainer: {
      display: 'flex',
      flexWrap: 'wrap' as const,
      gap: '8px',
      marginBottom: '24px',
    },
    produceTag: {
      padding: '8px 16px',
      backgroundColor: '#2D5A27',
      color: '#fff',
      borderRadius: '20px',
      fontSize: '14px',
      fontWeight: '600',
      textDecoration: 'none',
      transition: 'all 0.3s ease',
      display: 'inline-flex',
      alignItems: 'center',
    } as React.CSSProperties,
    produceTagHover: {
      backgroundColor: '#1a3d1a',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
    leafIcon: {
      marginRight: '6px',
      width: '16px',
      height: '16px',
    },
  };

  // Create clickable ingredient links
  const renderIngredient = (ingredient: any, index: number) => {
    // Parse ingredient structure
    const ingredientName = typeof ingredient === 'string' ? ingredient : ingredient.name || '';
    const amount = typeof ingredient === 'object' && ingredient.amount ? ingredient.amount : '';
    const unit = typeof ingredient === 'object' && ingredient.unit ? ingredient.unit : '';

    // Check if this ingredient matches a produce item (from primaryProduce)
    const isProduce = recipe.primaryProduce.some((produce) =>
      produce.toLowerCase() === ingredientName.toLowerCase() ||
      ingredientName.toLowerCase().includes(produce.toLowerCase())
    );

    return (
      <li
        key={index}
        style={styles.ingredientItem}
        onMouseEnter={(e) => {
          Object.assign(e.currentTarget.style, styles.ingredientItemHover);
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = '#f9f9f9';
          e.currentTarget.style.transform = 'translateX(0)';
        }}
      >
        <span style={styles.ingredientBullet}></span>
        <span>
          {amount && unit && `${amount} ${unit} `}
          {isProduce ? (
            <Link
              to={linkingHelper.getProductUrl(ingredientName)}
              style={styles.produceLink}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#1a3d1a';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#2D5A27';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              {ingredientName}
            </Link>
          ) : (
            ingredientName
          )}
        </span>
      </li>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <ChefHat style={styles.headerIcon} size={28} />
        <h2 style={styles.title}>Recipe Details</h2>
      </div>

      {/* Quick Meta Info */}
      <div style={styles.metaGrid}>
        <div style={styles.metaItem}>
          <Clock style={styles.metaIcon} size={24} />
          <div style={styles.metaLabel}>Prep Time</div>
          <div style={styles.metaValue}>{prepTime} min</div>
        </div>
        <div style={styles.metaItem}>
          <Clock style={styles.metaIcon} size={24} />
          <div style={styles.metaLabel}>Cook Time</div>
          <div style={styles.metaValue}>{cookTime} min</div>
        </div>
        <div style={styles.metaItem}>
          <Clock style={styles.metaIcon} size={24} />
          <div style={styles.metaLabel}>Total Time</div>
          <div style={styles.metaValue}>{totalTime} min</div>
        </div>
        <div style={styles.metaItem}>
          <Users style={styles.metaIcon} size={24} />
          <div style={styles.metaLabel}>Servings</div>
          <div style={styles.metaValue}>{recipe.servings}</div>
        </div>
      </div>

      {/* Description */}
      {recipe.description && (
        <p style={styles.description}>{recipe.description}</p>
      )}

      {/* Primary Produce Tags - KEY for bidirectional linking */}
      {recipe.primaryProduce && recipe.primaryProduce.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <Leaf style={styles.sectionIcon} size={20} />
            Main Ingredients
          </h3>
          <div style={styles.produceTagsContainer}>
            {recipe.primaryProduce.map((produce, index) => (
              <Link
                key={index}
                to={linkingHelper.getProductUrl(produce)}
                style={styles.produceTag}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, styles.produceTagHover);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#2D5A27';
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <Leaf style={styles.leafIcon} />
                {produce.charAt(0).toUpperCase() + produce.slice(1)}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Full Ingredients List */}
      {recipe.ingredients && recipe.ingredients.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <Leaf style={styles.sectionIcon} size={20} />
            Ingredients
          </h3>
          <ul style={styles.ingredientList}>
            {recipe.ingredients.map((ingredient, index) =>
              renderIngredient(ingredient, index)
            )}
          </ul>
        </div>
      )}

      {/* Instructions */}
      {recipe.instructions && recipe.instructions.length > 0 && (
        <div style={styles.section}>
          <h3 style={styles.sectionTitle}>
            <ChefHat style={styles.sectionIcon} size={20} />
            Instructions
          </h3>
          <ol style={styles.instructionList}>
            {recipe.instructions.map((instruction, index) => (
              <li key={index} style={styles.instructionItem}>
                <span style={styles.instructionNumber}>{index + 1}</span>
                {instruction}
              </li>
            ))}
          </ol>
        </div>
      )}

      {/* Dietary Tags */}
      {recipe.dietaryTags && recipe.dietaryTags.length > 0 && (
        <div style={styles.tagsContainer}>
          {recipe.dietaryTags.map((tag, index) => (
            <span key={index} style={styles.tag}>
              {tag}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

export default RecipeDetails;
